import React from 'react';

import { cn } from '@/lib/utils';

import { gradientBorderStyling } from '@/styles/gradient';
import { Colors } from '@/styles/types';
import { passDataTrackerRef } from '../button/utils';

export const sizesOptions = ['s', 'm', 'l'] as const;
const sizes: Record<(typeof sizesOptions)[number], string> = {
  s: 'px-1.5 py-[1px] text-label-text-m',
  m: 'px-2 py-1 text-label-text-m',
  l: 'px-2 py-1 text-label-text-l',
};

// TODO with gradientBorderStyling and think about dynamic solution that it would add 1px to all existing padding
const gradientExtraSizes: Record<(typeof sizesOptions)[number], string> = {
  s: 'px-[9px] py-[3px]',
  m: 'px-[9px] py-[5px]',
  l: 'px-[9px] py-[5px]',
};
export const variantOptions = ['filled', 'outlined'] as const;
export const colorsOptions = [
  'info-I99',
  'brand-B99',
  'neutral-N99',
  'neutral-N20',
  'primary-P99',
  'gradient',
  'lightGradient',
] satisfies Colors[];

export interface BadgeProps {
  variant?: (typeof variantOptions)[number];
  size?: keyof typeof sizes;
  leftSection?: React.ReactNode;
  rightSection?: React.ReactNode;
  colors?: (typeof colorsOptions)[number];
  classNames?: {
    root?: string;
  };
}

interface BadgePropsWithHtml
  extends BadgeProps,
  React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export default function Badge({
  children,
  classNames,
  colors = 'brand-B99',
  variant = 'filled',
  size = 'm',
  leftSection,
  rightSection,
  ...rest
}: BadgePropsWithHtml) {
  const variantClass = {
    filled: `text-neutral-N99 bg-${colors}`,
    outlined: `text-${colors} bg-transparent`,
  }[variant];

  const colorClasses = {
    'info-I99':
      'text-info-I99 group-hover:bg-info-I99 group-hover:text-neutral-N99',
    'primary-P99':
      'text-primary-P99 group-hover:bg-primary-P99 group-hover:text-neutral-N99',
    'brand-B99':
      'text-brand-B99 group-hover:bg-brand-B99 group-hover:text-neutral-N99',
    'neutral-N99':
      'text-neutral-N99 transition duration-700 ease-in-out group-hover:bg-neutral-N99 group-hover:text-neutral-N00 group-hover:border-transparent hover:bg-neutral-N99 hover:text-neutral-N00 hover:border-transparent',
    'neutral-N20':
      'text-neutral-N99 transition duration-700 ease-in-out group-hover:bg-neutral-N99 group-hover:text-neutral-N00 group-hover:border-transparent hover:bg-neutral-N99 hover:text-neutral-N00 hover:border-transparent dark:border-neutral-dark-N80',
    gradient: 'text-neutral-N99',
    lightGradient: 'text-neutral-N99',
  } as const;
  const specificColorClasses =
    (variant === 'outlined' && colorClasses?.[colors]) || '';

  return (
    <div
      {...passDataTrackerRef<HTMLDivElement>()}
      className={`${cn(
        `flex w-fit items-center justify-center gap-1.5 whitespace-nowrap`,
        variantClass,
        specificColorClasses,
        sizes[size],
        // TODO I really don't like this solution, need to invest time to think once more and that it could be reused
        colors === 'gradient' && variant === 'filled'
          ? `${gradientExtraSizes[size]}`
          : `border-${colors}`,
        colors === 'gradient' && variant === 'outlined'
          ? `${gradientBorderStyling({
            borderWidth: 'p-px',
            borderRadius: 'rounded-3xl',
          })} ${gradientExtraSizes[size]} relative`
          : `rounded-3xl`,
        variant === 'outlined' &&
        colors !== 'gradient' &&
        'border border-solid',
        colors === 'neutral-N99' && variant === 'filled' && 'text-neutral-N00',
        classNames?.root
      )}`}
      data-tracker-component='badge'
      {...rest}
    >
      {leftSection}
      {children}
      {rightSection}
    </div>
  );
}
